import React, { CSSProperties } from "react";
import { TextFieldProps } from "@mui/material/TextField";
import { TooltipProps } from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import { Tooltip } from "components/mui-styled-components/Tooltip";

import { useController } from "react-hook-form";

import { useSmartFormContext } from "components/SmartForm";
import HelperText from "../HelperText";
import * as S from "./styles";

export type SmartFormTextFieldT = {
  label?: string;
  name: string;
  helperText?: string | undefined;
  error?: boolean;
  disabled?: boolean;
  labelStyle?: CSSProperties;
  required?: boolean;
  prefixIcon?: React.ReactNode;
  type?: string;
  fieldPrefix?: React.ReactNode;
  prefixBorder?: boolean;
  fieldSuffix?: React.ReactNode;
  suffixBorder?: boolean;
  fullWidth?: boolean;
  tooltipText?: string | JSX.Element;
  isLargeText?: boolean;
  noMarginBlockEnd?: boolean;
  hasRedHighlight?: boolean;
  hasBlueHighlight?: boolean;
  hideLabelWrapper?: boolean;
  tooltipPlacement?: TooltipProps["placement"];
} & TextFieldProps;

const SmartFormTextField = ({
  label,
  name,
  helperText,
  error,
  disabled = false,
  labelStyle = {},
  required,
  type = "text",
  fullWidth = true,
  fieldPrefix,
  prefixBorder = true,
  fieldSuffix,
  suffixBorder = true,
  tooltipText,
  isLargeText = false,
  noMarginBlockEnd = false,
  hasRedHighlight = false,
  hasBlueHighlight = false,
  hideLabelWrapper = false,
  tooltipPlacement = "top",
  sx,
  ...rest
}: SmartFormTextFieldT) => {
  const { controllerProps } = useSmartFormContext();
  const { field } = useController({ ...controllerProps, name });

  return (
    <>
      {!hideLabelWrapper && (
        <S.LabelGroupWrapper>
          <S.StyledFormLabel style={labelStyle}>{label}</S.StyledFormLabel>
          {required && <S.StyledRequiredLabel>*</S.StyledRequiredLabel>}
          {tooltipText && (
            <Tooltip placement={tooltipPlacement} title={<S.TooltipText>{tooltipText}</S.TooltipText>}>
              <S.TooltipIcon>!</S.TooltipIcon>
            </Tooltip>
          )}
        </S.LabelGroupWrapper>
      )}
      <S.StyledTextField
        {...field}
        {...rest}
        fullWidth={fullWidth}
        hasSuffix={!!fieldSuffix}
        isLargeText={isLargeText}
        noMarginBlockEnd={noMarginBlockEnd}
        redHighlight={hasRedHighlight}
        blueHighlight={hasBlueHighlight}
        InputProps={{
          ...rest.InputProps,
          ...(fieldPrefix && {
            startAdornment: (
              <InputAdornment position="start">
                <S.PrefixWrapper border={prefixBorder}>{fieldPrefix}</S.PrefixWrapper>
              </InputAdornment>
            ),
          }),
          ...(fieldSuffix && {
            endAdornment: (
              <InputAdornment position="end">
                <S.SuffixWrapper border={suffixBorder}>{fieldSuffix}</S.SuffixWrapper>
              </InputAdornment>
            ),
          }),
        }}
        data-test-id={`${name}-SmartFormTextField`}
        disabled={disabled}
        type={type}
        size="small"
        defaultValue={rest.defaultValue}
        sx={{ backgroundColor: "white", borderRadius: "4px", ...sx }}
        value={field.value}
      />
      <HelperText sx={{ marginTop: "0px" }} error={error}>
        {helperText}
      </HelperText>
    </>
  );
};

export default SmartFormTextField;
